<script setup>
import {Link, router} from '@inertiajs/vue3';
import {useWindowSize} from "@vueuse/core";
import {onBeforeUnmount, onMounted, ref, watch} from "vue";
import LogoIconComponent from "@/Components/LogoIconComponent.vue";
import RegisterButton from "@/Components/Buttons/RegisterButton.vue";
import GoogleTooltipComponent from "@/Components/GoogleTooltipComponent.vue";
import LinkedInIcon from "@/Components/Icons/LinkedInIcon.vue";
import InstagramIcon from "@/Components/Icons/InstagramIcon.vue";
import YoutubeIcon from "@/Components/Icons/YoutubeIcon.vue";
import TikTokIcon from "@/Components/Icons/TikTokIcon.vue";
import FacebookIcon from "@/Components/Icons/FacebookIcon.vue";
import TwitterXIcon from "@/Components/Icons/TwitterXIcon.vue";

const {width} = useWindowSize()
const isMobileMenuOpen = ref(false), menu = ref(null)

watch(width, (current, previous) => {
    if (current >= 768 && previous < 768) {
        if (isMobileMenuOpen.value) {
            closeMobileMenu();
        }
    }
})

const menuItems = ref([
    { title: 'Cases', scrollRef: 'whatIsGoogleAdsRef' },
    { title: 'How it works', scrollRef: 'howItWorksRef' },
    { title: 'Price', scrollRef: 'pricingRef' },
    { title: 'Contacts', scrollRef: 'contactsRef' },
]);

const scrollToContainer = containerToScroll => {
    if (isMobileMenuOpen.value) {
        closeMobileMenu();
    }

    if (route().current('home')) {
        window.scrollTo({
            top: document.querySelector('[data-anchor=' + containerToScroll + ']')?.offsetTop,
            behavior: 'smooth'
        })
        return;
    }
    router.visit(route('home'), {
        onFinish: () =>
            window.scrollTo({
                top: document.querySelector('[data-anchor=' + containerToScroll + ']')?.offsetTop,
                behavior: 'smooth'
            })
    })
}

const closeMobileMenu = () => {
    document.body.removeAttribute('style')
    document.body.querySelector('#app').removeAttribute('style')
    menu.value.removeAttribute('style');
    isMobileMenuOpen.value = false
}

const onMenuToggle = () => {
    if (isMobileMenuOpen.value) {
        closeMobileMenu();
        return;
    }

    document.body.style.overflow = 'hidden';
    document.body.querySelector('#app').style.overflow = 'hidden';
    menu.value.style.display = 'block'
    isMobileMenuOpen.value = true;
}

onMounted(() => {
    if (!document.body.classList.contains('bg-[#FCFBFF]')) document.body.classList.add('bg-[#FCFBFF]');
});

onBeforeUnmount(() => {
    if (document.body.classList.contains('bg-[#FCFBFF]')) document.body.classList.remove('bg-[#FCFBFF]');
});
</script>

<template>
    <header>
        <nav class="container py-4 md:py-5 mx-auto">
            <div class="flex justify-between items-center text-grey-700">
                <button
                    v-if="width < 768"
                    class="flex justify-center flex-col items-center bg-white rounded-[10px] h-12 w-12 transition p-3.5 shadow-custom hover:shadow-sm"
                    aria-label="Burger button"
                    role="button"
                    @click="onMenuToggle"
                >
                    <span
                        class="bg-black block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm"
                        :class="{
                            'rotate-45 translate-y-1': isMobileMenuOpen,
                            '-translate-y-0.5': !isMobileMenuOpen
                        }"
                    ></span>
                    <span
                        class="bg-black block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm my-0.5"
                        :class="{
                            'opacity-0': isMobileMenuOpen,
                            'opacity-100': !isMobileMenuOpen
                        }"
                    ></span>
                    <span
                        class="bg-black block transition-all duration-300 ease-out h-0.5 w-5 rounded-sm"
                        :class="{
                            '-rotate-45 -translate-y-1': isMobileMenuOpen,
                            'translate-y-0.5': !isMobileMenuOpen
                        }"
                    ></span>
                </button>

                <LogoIconComponent />

                <div
                    ref="menu"
                    class="max-md:bottom-0 max-md:inset-x-0 max-md:top-[74px] bg-[#FCFBFF] z-50 max-md:fixed hidden md:block"
                >
                    <ul class="flex flex-col md:flex-row items-center justify-start max-md:h-full max-md:space-y-3 max-md:text-2xl max-md:pt-20 max-md:px-16 md:justify-center md:space-x-3 lg:space-x-5 md:ml-5 lg:ml-16">
                        <li
                            v-for="menuItem in menuItems"
                            :key="menuItem.title"
                            class="max-md:w-full max-md:max-w-60"
                        >
                            <a
                                class="px-3 py-3.5 relative block before:content-[''] text-center before:absolute before:bottom-0 before:left-0 before:w-0 before:h-[2px] md:before:bg-yellow-500 hover:before:w-full hover:text-grey-900 before:transition-all before:duration-300 ease-in-out"
                                href="#"
                                @click.prevent="scrollToContainer(menuItem.scrollRef)"
                            >{{ menuItem.title }}</a>
                        </li>
                        <li
                            class="max-md:w-full max-md:max-w-60"
                        >
                            <a
                                class="px-3 py-3.5 relative block before:content-[''] text-center before:absolute before:bottom-0 before:left-0 before:w-0 before:h-[2px] md:before:bg-yellow-500 hover:before:w-full hover:text-grey-900 before:transition-all before:duration-300 ease-in-out"
                                :href="route('blog.index')"
                            >Blog</a>
                        </li>
                        <template v-if="width <= 767">
                            <li class="max-md:w-full max-md:max-w-60 !mt-auto">
                                <Link
                                    class="mt-auto max-md:text-center max-md:space-y-3 max-md:text-2xl px-3 py-3.5 md:ml-auto md:mr-5 block hover:text-grey-900"
                                    :href="route('login')"
                                >Login</Link>
                            </li>
                            <li class="flex justify-center max-md:w-full !mb-16">
                                <RegisterButton burger-menu-item />
                            </li>
                        </template>
                    </ul>
                </div>

                <a
                    v-if="width > 767"
                    class="px-3 py-3.5 md:ml-auto md:mr-5 relative block before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-0 before:h-[2px] md:before:bg-yellow-500 hover:before:w-full hover:text-grey-900 before:transition-all before:duration-300 ease-in-out"
                    :href="route('login')"
                    aria-label="Go to login"
                >Login</a>

                <RegisterButton />
            </div>
        </nav>
    </header>

    <slot></slot>

    <footer
        data-anchor="contactsRef"
        class="container py-8 lg:pb-5 mx-auto"
    >
        <div class="flex flex-col items-center gap-7 lg:flex-row lg:justify-between xl:gap-12 2xl:gap-16 color-grey-800 leading-tight border-b-[1px] border-grey-100 pt-6 pb-8">
            <div class="flex flex-col md:flex-row max-lg:w-full items-center justify-center gap-x-4 gap-y-5 lg:mr-auto">
                <LogoIconComponent
                    in-footer
                />
                <GoogleTooltipComponent
                    class="lg:hidden xl:block"
                    google-icon
                    :bottom="width < 768"
                />
            </div>

            <div class="flex flex-col md:flex-row gap-3 items-center">
                <span class="inline-block text-base font-semibold">
                    Contact
                </span>

                <a
                    href="mailto:ceo@miob.io"
                    target="_blank"
                    aria-label="Create email"
                    class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md px-2.5 py-2"
                >
                    <picture>
                        <source
                            type="image/webp"
                            srcset="@images/ceo.webp"
                        />
                        <source
                            type="image/jpeg"
                            srcset="@images/ceo.jpg"
                        />
                        <img
                            src="@images/ceo.jpg"
                            alt=""
                            loading="lazy"
                            class="w-8 h-8 rounded-full mr-2.5"
                        >
                    </picture>

                    <span>
                        ceo@miob.io
                    </span>
                </a>
            </div>

            <div class="flex flex-col md:flex-row gap-3 items-center">
                <span class="inline-block text-base font-semibold mb-2 md:mb-0">
                    Follow @miob
                </span>

                <div class="inline-flex gap-1">
                    <a
                        href="https://www.linkedin.com/company/miob-io"
                        target="_blank"
                        aria-label="Go to Miob on linkedin"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <LinkedInIcon class="h-6 w-6 fill-black" />
                    </a>

                    <a
                        href="https://www.instagram.com/miobcorp/"
                        target="_blank"
                        aria-label="Go to Miob on instagram"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <InstagramIcon class="h-6 w-6 fill-black" />
                    </a>

                    <a
                        href="https://www.youtube.com/@miob_io"
                        target="_blank"
                        aria-label="Go to Miob on youtube"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <YoutubeIcon class="h-6 w-6 fill-black" />
                    </a>

                    <a
                        href="https://www.tiktok.com/@miob.io?_t=8rIxqJegcLY&_r=1"
                        target="_blank"
                        aria-label="Go to Miob on tiktok"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <TikTokIcon class="h-6 w-6 fill-black" />
                    </a>

                    <a
                        href="https://www.facebook.com/miobcorp"
                        target="_blank"
                        aria-label="Go to Miob on facebook"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <FacebookIcon class="h-6 w-6 fill-black" />
                    </a>

                    <a
                        href="https://x.com/miobcorp"
                        target="_blank"
                        aria-label="Go to Miob on X (twitter)"
                        class="flex items-center text-lg font-semibold rounded-lg bg-white shadow-[0px_2px_8px_-12px_#1511301F] hover:shadow-md active: p-2"
                    >
                        <TwitterXIcon class="h-6 w-6 fill-black" />
                    </a>
                </div>
            </div>
        </div>

        <div class="text-center lg:flex flex-row-reverse justify-between mt-6">
            <ul class="flex flex-col sm:flex-row items-center sm:justify-center gap-4 sm:gap-14 lg:gap-9">
                <li
                    v-for="menuItem in menuItems"
                    :key="menuItem.title"
                    class=""
                >
                    <a
                        class="block text-grey-700 leading-[22px] hover:opacity-75"
                        href="#"
                        :aria-label="`Go to ${menuItem.title} page section`"
                        @click.prevent="scrollToContainer(menuItem.scrollRef)"
                    >{{ menuItem.title }}</a>
                </li>
            </ul>

            <div class="flex justify-between sm:justify-center sm:gap-8 w-full lg:w-fit text-grey-700 text-sm space-x-1.5 max-lg:mt-8">
                <Link
                    :href="route('terms.show')"
                    aria-label="Go to Miob Terms & Conditions"
                    class="hover:opacity-75"
                >Terms & Conditions</Link>
                <Link
                    :href="route('policy.show')"
                    aria-label="Go to Miob Privacy & Cookies"
                    class="hover:opacity-75"
                >Privacy & Cookies</Link>
                <Link
                    :href="route('platform_rules.show')"
                    aria-label="Go to Miob Platform Rules"
                    class="hover:opacity-75"
                >Platform Rules</Link>
            </div>
        </div>
    </footer>
</template>
